import { CircleX } from '@/components/ui/icons';
import { MessageItem } from '@/types/misc';

interface AttachmentThumbnailStripProps {
  images: MessageItem[];
  onRemove: (id: string) => void;
}

/**
 * A compact strip of uploaded-image thumbnails, shown directly under the prompt
 * input. `MessageItem.url` is already a base64 data-URL (set by the upload hook),
 * so each tile renders with no extra URL resolution. In-flight uploads show a
 * spinner; hovering a tile reveals a remove button.
 */
export function AttachmentThumbnailStrip({
  images,
  onRemove,
}: AttachmentThumbnailStripProps) {
  if (images.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2 px-1">
      {images.map((img) => (
        <div key={img.id} className="group relative h-12 w-12 shrink-0">
          {img.url ? (
            <img
              src={img.url}
              alt="Uploaded reference"
              className="h-12 w-12 rounded-md border border-telekinesis-neutral-700 object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-md border border-telekinesis-neutral-700 bg-telekinesis-background-2" />
          )}
          {img.isUploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(img.id)}
            aria-label="Remove image"
            className="absolute -right-1.5 -top-1.5 hidden rounded-full bg-telekinesis-background-1 text-telekinesis-neutral-50 hover:text-[#DA291C] group-hover:block"
          >
            <CircleX className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
